import React, { useEffect, useRef, useState } from "react";
import { autoImportSms, DiscoverySheet } from "./screens/SmsImport";
import { Home, CheckCircle, TrendingUp, User, Sun, Moon, RefreshCw, Plus, ArrowUpRight, ArrowDownLeft, Calendar, Bell, PieChart } from "react-feather";
import WalletOutIcon from "../components/WalletOutIcon";
import MoneyInOutIcon from "../components/MoneyInOutIcon";
import ExpenseAnalysis from "../analysis/ExpenseAnalysis";
import { monthBudgetStatus, BUDGET_WARN } from "../analysis/Budgets";
import { monthSummary, summaryText } from "../analysis/monthSummary";
import { formatAmount } from "../settings/SettingsContext";
import { todayIso } from "./ui";
import { useTheme } from "../theme/ThemeContext";
import { useSettings } from "../settings/SettingsContext";
import { useMobileData } from "./useMobileData";
import { useTasks } from "../tasks/useTasks";
import { useGroups } from "../groups/useGroups";
import { actionCount } from "../tasks/taskUtils";
import { syncReminders, onReminderTapped, notifyNow } from "../tasks/notifications";
import { useRealtime } from "../realtime/useRealtime";
import { startPush } from "../push/push";
import HomeScreen from "./screens/HomeScreen";
import ActivityScreen from "./screens/ActivityScreen";
import BillsScreen from "./screens/BillsScreen";
import TasksScreen, { TaskForm } from "./screens/TasksScreen";
import AddEntrySheet from "./screens/AddEntrySheet";
import { Sheet } from "./ui";
import AccountsScreen from "./screens/AccountsScreen";
import MoreScreen from "./screens/MoreScreen";
import NotificationsSheet, { reminderItems } from "./screens/NotificationsSheet";
import AdminDashboard from "../admin/AdminDashboard";
import { useMe } from "../admin/useMe";

const TABS = [
  { id: "home", label: "Home", Icon: Home, title: "budgetsprint" },
  { id: "activity", label: "Activity", Icon: MoneyInOutIcon, title: "Activity" },
  { id: "bills", label: "Bills", Icon: WalletOutIcon, title: "Bills & Payments" },
  { id: "tasks", label: "Tasks", Icon: CheckCircle, title: "Tasks" },
  // Accounts, investments, income and loans: everything you own, earn and owe.
  { id: "accounts", label: "Finances", Icon: TrendingUp, title: "Finances" },
  // "more"/You is not in the bottom bar - it's reached from the profile icon in the top bar.
  { id: "more", label: "You", Icon: User, title: "You" },
  // Fixed vs. random spending, reached from Home and the You screen.
  { id: "analysis", label: "Analysis", Icon: PieChart, title: "Analysis" },
  // Admins only, reached from the You screen.
  { id: "admin", label: "User analytics", Icon: User, title: "User analytics" },
];

// Bottom bar order, with Home in the middle and raised, like a pill-shaped dock.
// The + button floats separately, over the bottom-right corner of the screen.
const NAV_ORDER = ["activity", "bills", "home", "tasks", "accounts"];
// The Home circle is taller than the bar (~54px), so it pokes out top and bottom,
// lifted a little so more of it sits above the bar.
const HOME_SIZE = 72;
const HOME_LIFT = 3;

// On a screen that already means one specific thing, the + button skips the
// menu and opens that thing's form directly. Home has no single natural form,
// so it still shows the full menu.
const DIRECT_ADD = { bills: "bill", tasks: "task", activity: "activity" };

const ADD_CHOICES = [
  { id: "expense", label: "Expense", hint: "Money you spent", Icon: ArrowUpRight },
  { id: "income", label: "Money received", hint: "A one-time amount", Icon: ArrowDownLeft },
  { id: "bill", label: "Bill to pay", hint: "Something due later", Icon: Calendar },
  { id: "task", label: "Task", hint: "A reminder, can repeat", Icon: CheckCircle },
];

const QuickAddMenu = ({ onPick, onClose }) => {
  const { tokens, fonts } = useTheme();
  return (
    <Sheet title="What do you want to add?" onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {ADD_CHOICES.map(({ id, label, hint, Icon }) => (
          <button
            key={id}
            onClick={() => onPick(id)}
            style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, padding: 16, borderRadius: 16, border: `1px solid ${tokens.surfaceBorder}`, backgroundColor: tokens.bg, cursor: "pointer", textAlign: "left", color: tokens.textPrimary }}
          >
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: 12, backgroundColor: tokens.accentSoft, color: tokens.accent }}>
              <Icon size={20} />
            </span>
            <span>
              <span style={{ display: "block", fontFamily: fonts.display, fontSize: 16, fontWeight: 700 }}>{label}</span>
              <span style={{ display: "block", marginTop: 2, fontSize: 12, color: tokens.textMuted }}>{hint}</span>
            </span>
          </button>
        ))}
      </div>
    </Sheet>
  );
};
// The phone version of the app. It has its own screens and layout and only shares
// the login, API helper, theme and small utilities with the desktop version, so it
// can be changed freely without touching the desktop screens.
const MobileApp = () => {
  const { tokens, fonts, theme, toggleTheme } = useTheme();
  const { settings } = useSettings(); // also makes sure settings are loaded before screens read them
  const [tab, setTab] = useState("home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [adding, setAdding] = useState(null); // null | "expense" | "income" | "bill" | "task"
  const data = useMobileData();
  const tasksApi = useTasks();
  const groupsApi = useGroups();
  const me = useMe();
  const isAdmin = !!(me && me.isAdmin);
  const pendingTasks = actionCount(tasksApi.tasks);
  const bellCount = data.loading ? 0 : reminderItems({ bills: data.bills, income: data.income, tasks: tasksApi.tasks }).now.length;

  // Phone reminders: tasks when they're due, and every unpaid bill, EMI or
  // instalment at 6am each morning until it's marked paid.
  // Plus the month-end summary, planned for the 1st of next month.
  useEffect(() => {
    if (tasksApi.loading || data.loading) return;
    const now = new Date();
    const s = monthSummary(data.bills, data.income, now.getFullYear(), now.getMonth());
    const summary = { title: `${s.label} in numbers`, body: summaryText(s, (n) => formatAmount(n, settings.maskAmounts)) };
    syncReminders({ tasks: tasksApi.tasks, bills: data.bills, maskAmounts: settings.maskAmounts, summary });
  }, [tasksApi.tasks, tasksApi.loading, data.bills, data.income, data.loading, settings.maskAmounts]);

  // The monthly random-spending budget, and each category's, reaching 98% and again
  // when it goes over: one phone alert each, per month. What was already sent is
  // remembered on this phone.
  useEffect(() => {
    if (data.loading) return;
    const status = monthBudgetStatus(data.bills, data.budgets);
    const lines = [
      { id: "random", name: "Monthly budget", what: "your budget for one-off spending", ...status },
      ...status.categories.map((c) => ({ id: `cat:${c.name}`, name: `${c.name} budget`, what: `your ${c.name} budget`, ...c })),
    ];
    for (const { id, name, what, limit, spent, used } of lines) {
      if (!limit || used < BUDGET_WARN) continue;
      const level = used > 1 ? "over" : "98";
      const key = `budgetsprint-budget-alert:${todayIso().slice(0, 7)}:${id}:${level}`;
      try {
        if (localStorage.getItem(key)) continue;
      } catch {
        return; // can't remember it, so don't risk alerting on every change
      }
      const amounts = settings.maskAmounts ? "" : ` (${formatAmount(spent, false)} of ${formatAmount(limit, false)})`;
      notifyNow({
        key,
        title: level === "over" ? `${name} crossed` : `${name} at ${Math.round(used * 100)}%`,
        body: level === "over" ? `You've spent more than ${what} this month${amounts}.` : `You've used ${Math.round(used * 100)}% of ${what} this month${amounts}.`,
      }).then((shown) => {
        // Only once it has actually been shown (not in a browser, or before permission).
        if (!shown) return;
        try {
          localStorage.setItem(key, "1");
        } catch {
          // ignore
        }
      });
    }
  }, [data.bills, data.budgets, data.loading, settings.maskAmounts]);

  // New bank SMS are added by themselves: once the data has loaded, and again whenever
  // the app comes back to the front (at most once a minute). See autoImportSms.
  const [smsToast, setSmsToast] = useState(null);
  const dataRef = useRef(data);
  dataRef.current = data;
  useEffect(() => {
    if (data.loading) return undefined;
    let lastRun = 0;
    const run = async () => {
      if (Date.now() - lastRun < 60000) return;
      lastRun = Date.now();
      const result = await autoImportSms(dataRef.current);
      if (!result) return;
      if (result.added > 0) dataRef.current.reload();
      // A new card, salary or investment is asked about (tap opens the questions);
      // otherwise just say what was added.
      const finds = result.discoveries || [];
      if (finds.length) {
        const what = [...new Set(finds.map((f) => f.kind))].map((k) => ({ card: "a card", salary: "your salary", investment: "an investment" }[k])).join(", ");
        setSmsToast({ text: `${result.added ? `Added ${result.added} from your SMS. ` : ""}Found ${what} to add. Tap to review.`, finds });
      } else if (result.added > 0) {
        setSmsToast({ text: `Added ${result.added} ${result.added === 1 ? "entry" : "entries"} from your SMS${result.failed ? ` (${result.failed} couldn't be saved)` : ""}. Tap to see them.` });
      }
    };
    run();
    const onVisible = () => document.visibilityState === "visible" && run();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // Only when loading finishes; the latest data is read through dataRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.loading]);
  useEffect(() => {
    if (!smsToast) return undefined;
    const timer = setTimeout(() => setSmsToast(null), smsToast.finds ? 15000 : 8000);
    return () => clearTimeout(timer);
  }, [smsToast]);
  const [smsFinds, setSmsFinds] = useState(null);

  // Tapping a payment reminder opens the reminders panel (to mark it paid); the
  // month summary or a budget alert opens Analysis; a task reminder or push
  // notification opens the Tasks screen.
  useEffect(() => onReminderTapped((kind) => (kind === "bill" ? setBellOpen(true) : setTab(kind === "analysis" ? "analysis" : "tasks"))), []);
  useEffect(() => {
    let stop = () => {};
    startPush({ onTap: () => setTab("tasks") }).then((fn) => { stop = fn; });
    return () => stop();
  }, []);

  // Live updates: something changed on the server (for example the other person
  // confirmed a task), so refresh straight away and show a banner if it is for me.
  const [toast, setToast] = useState(null);
  const reloadTasks = tasksApi.reload;
  const reloadGroups = groupsApi.reload;
  useRealtime((event) => {
    if (event.kind !== "groups") reloadTasks();
    if (event.kind === "groups" || event.kind === "resync") reloadGroups();
    if (event.message) setToast({ title: event.title, message: event.message });
  });
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 7000);
    return () => clearTimeout(timer);
  }, [toast]);

  const refresh = () => {
    data.reload();
    tasksApi.reload();
    groupsApi.reload();
  };

  const current = TABS.find((t) => t.id === tab);

  // The middle (Home) tab is a raised circle, popping up out of the pill like the
  // old + button did; the other four are plain icon buttons, no labels, to match
  // the reference dock layout.
  const renderTab = (id) => {
    const { label, Icon } = TABS.find((t) => t.id === id);
    const active = tab === id;

    // Home is a bigger circle, centred on the bar so it sticks out equally above and below.
    if (id === "home") {
      return (
        <div key={id} style={{ width: HOME_SIZE + 4, flexShrink: 0, alignSelf: "stretch", position: "relative", display: "flex", justifyContent: "center" }}>
          <button
            onClick={() => { setTab(id); window.scrollTo(0, 0); }}
            aria-label={label}
            aria-current={active ? "page" : undefined}
            style={{
              position: "absolute",
              top: "50%",
              // Centred on the bar, then nudged up so more of it shows above.
              transform: `translateY(calc(-50% - ${HOME_LIFT}px))`,
              width: HOME_SIZE,
              height: HOME_SIZE,
              borderRadius: HOME_SIZE / 2,
              border: `4px solid ${tokens.bg}`,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              padding: 0,
              backgroundColor: tokens.accent,
              color: tokens.accentContrast,
              boxShadow: "0 8px 18px rgba(0,0,0,0.3)",
            }}
          >
            <Icon size={28} strokeWidth={2.4} />
          </button>
        </div>
      );
    }

    return (
      <button
        key={id}
        onClick={() => { setTab(id); window.scrollTo(0, 0); }}
        aria-current={active ? "page" : undefined}
        style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, padding: "4px 0 3px", background: "none", border: "none", cursor: "pointer", color: active ? tokens.accent : tokens.textMuted }}
      >
        <span style={{ position: "relative", display: "inline-flex" }}>
          <Icon size={22} />
          {id === "tasks" && pendingTasks > 0 && (
            <span style={{ position: "absolute", top: -5, right: -9, minWidth: 16, height: 16, padding: "0 4px", boxSizing: "border-box", borderRadius: 8, fontSize: 10, lineHeight: "16px", textAlign: "center", backgroundColor: tokens.negative, color: "#fff" }}>{pendingTasks}</span>
          )}
        </span>
        <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 600, lineHeight: "13px", whiteSpace: "nowrap" }}>{label}</span>
      </button>
    );
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: tokens.bg, fontFamily: fonts.body, color: tokens.textPrimary }}>
      {toast && (
        <button
          onClick={() => { setTab("tasks"); setToast(null); }}
          style={{ position: "fixed", top: "calc(8px + env(safe-area-inset-top))", left: 12, right: 12, zIndex: 3000, textAlign: "left", padding: "12px 14px", borderRadius: 14, border: "none", cursor: "pointer", backgroundColor: tokens.accent, color: tokens.accentContrast, boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
        >
          <div style={{ fontSize: 13, fontWeight: 800 }}>{toast.title}</div>
          <div style={{ fontSize: 13, marginTop: 2 }}>{toast.message}</div>
        </button>
      )}
      {smsToast && !toast && (
        <button
          onClick={() => {
            if (smsToast.finds) setSmsFinds(smsToast.finds);
            else { setTab("activity"); window.scrollTo(0, 0); }
            setSmsToast(null);
          }}
          style={{ position: "fixed", top: "calc(8px + env(safe-area-inset-top))", left: 12, right: 12, zIndex: 3000, textAlign: "left", padding: "12px 14px", borderRadius: 14, border: "none", cursor: "pointer", backgroundColor: tokens.accent, color: tokens.accentContrast, boxShadow: "0 8px 24px rgba(0,0,0,0.35)", fontSize: 13, fontWeight: 700 }}
        >
          {smsToast.text}
        </button>
      )}
      {smsFinds && <DiscoverySheet items={smsFinds} data={data} onClose={() => setSmsFinds(null)} />}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "calc(12px + env(safe-area-inset-top)) 16px 12px",
          backgroundColor: tokens.topbarBg,
          color: tokens.topbarText,
        }}
      >
        <h1 style={{ margin: 0, fontFamily: fonts.display, fontSize: 20 }}>{current.title}</h1>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setBellOpen(true)} aria-label={bellCount ? `Reminders, ${bellCount} need attention` : "Reminders"} style={{ ...iconBtn(tokens), position: "relative" }}>
            <Bell size={18} />
            {bellCount > 0 && (
              <span style={{ position: "absolute", top: 2, right: 2, minWidth: 16, height: 16, padding: "0 4px", boxSizing: "border-box", borderRadius: 8, fontSize: 10, fontWeight: 700, lineHeight: "16px", textAlign: "center", backgroundColor: tokens.negative, color: "#fff" }}>
                {bellCount > 99 ? "99+" : bellCount}
              </span>
            )}
          </button>
          <button onClick={refresh} aria-label="Refresh" style={iconBtn(tokens)}><RefreshCw size={18} /></button>
          <button onClick={toggleTheme} aria-label="Toggle theme" style={iconBtn(tokens)}>
            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
          </button>
          <button
            onClick={() => { setTab("more"); window.scrollTo(0, 0); }}
            aria-label="You: profile, groups and settings"
            style={{ ...iconBtn(tokens), ...(tab === "more" ? { backgroundColor: tokens.accent, color: tokens.accentContrast } : {}) }}
          >
            <User size={18} />
          </button>
        </div>
      </header>

      <main style={{ padding: "12px 16px calc(104px + env(safe-area-inset-bottom))", maxWidth: 560, margin: "0 auto" }}>
        {data.error && (
          <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, fontSize: 13, backgroundColor: tokens.pendingBg, color: tokens.pending }}>
            Couldn't reach the server. Check your connection and tap refresh.
          </div>
        )}
        {data.loading ? (
          <p style={{ textAlign: "center", color: tokens.textMuted, marginTop: 40 }}>Loading…</p>
        ) : (
          <>
            {tab === "home" && <HomeScreen data={data} goTo={(t) => { setTab(t); window.scrollTo(0, 0); }} />}
            {tab === "activity" && <ActivityScreen data={data} />}
            {tab === "bills" && <BillsScreen data={data} />}
            {tab === "tasks" && <TasksScreen tasksApi={tasksApi} groupsApi={groupsApi} />}
            {tab === "accounts" && <AccountsScreen data={data} />}
            {tab === "more" && <MoreScreen data={data} groupsApi={groupsApi} isAdmin={isAdmin} goTo={(t) => { setTab(t); window.scrollTo(0, 0); }} />}
            {tab === "analysis" && <ExpenseAnalysis bills={data.bills} budgets={data.budgets} budgetApi={data} />}
            {tab === "admin" && isAdmin && <AdminDashboard />}
          </>
        )}
      </main>

      {/* A floating pill, not full-width, so it reads as a dock rather than a bar. */}
      <nav
        style={{
          position: "fixed",
          left: 16,
          right: 16,
          bottom: "calc(14px + env(safe-area-inset-bottom))",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          padding: "4px 10px",
          borderRadius: 28,
          backgroundColor: tokens.surface,
          border: `1px solid ${tokens.surfaceBorder}`,
          boxShadow: "0 10px 28px rgba(0,0,0,0.18)",
        }}
      >
        {NAV_ORDER.map(renderTab)}
      </nav>

      {/* Floating add button, pinned to the bottom-right of the screen. On a screen
          that maps to one kind of entry, it opens that form directly. Hidden on
          You/Accounts, which have their own dedicated "+ Add" buttons instead. */}
      {tab !== "more" && tab !== "accounts" && tab !== "admin" && (
        <button
          onClick={() => (DIRECT_ADD[tab] ? setAdding(DIRECT_ADD[tab]) : setMenuOpen(true))}
          aria-label="Add something"
          style={{ position: "fixed", right: 18, bottom: "calc(96px + env(safe-area-inset-bottom))", width: 56, height: 56, borderRadius: 28, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: tokens.accent, color: tokens.accentContrast, boxShadow: "0 6px 18px rgba(0,0,0,0.3)", zIndex: 900 }}
        >
          <Plus size={26} strokeWidth={2.6} />
        </button>
      )}

      {bellOpen && <NotificationsSheet data={data} tasksApi={tasksApi} goTo={(t) => { setTab(t); window.scrollTo(0, 0); }} onClose={() => setBellOpen(false)} />}
      {menuOpen && <QuickAddMenu onClose={() => setMenuOpen(false)} onPick={(choice) => { setMenuOpen(false); setAdding(choice); }} />}
      {adding && adding !== "task" && <AddEntrySheet key={adding} mode={adding} data={data} onClose={() => setAdding(null)} />}
      {adding === "task" && <TaskForm task={null} tasksApi={tasksApi} groupsApi={groupsApi} onClose={() => setAdding(null)} />}
    </div>
  );
};

const iconBtn = (tokens) => ({
  width: 38,
  height: 38,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 19,
  border: "none",
  cursor: "pointer",
  backgroundColor: tokens.topbarBgActive,
  color: tokens.topbarText,
});

export default MobileApp;
